import React, { useEffect, useState, useRef } from 'react'
import { FixedSizeList } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import { Mission } from '../../types/missionTypes'
import { useSelector } from 'react-redux'
import { useAppDispatch, RootState } from '../../redux/store'
import { getMissions } from '../../redux/missions/missionActions'
import { Loader } from 'rsuite'

const Select: React.FC = () => {
  const dispatch = useAppDispatch()
  const [missions, setMissions] = useState<Mission[]>([])
  const missionList = useSelector(
    (state: RootState) => state.mission.MissionResponse
  )
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)
  const [hasMore, setHasMore] = useState<boolean>(false)
  const observerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setHasMore(
      missionList?.totalPages ? currentPage < missionList.totalPages : false
    )
    if (Array.isArray(missionList?.missions)) {
      setMissions((prevMissions) => [
        ...prevMissions,
        ...(missionList?.missions || []),
      ])
    }
  }, [missionList])

  useEffect(() => {
    // Charger initialement la page 1
    dispatch(getMissions({ page: 1, pageSize: 3 }))
  }, [dispatch])

  const loadMoreItems = async () => {
    if (currentPage < (missionList?.totalPages || 0) && !loading) {
      setLoading(true)
      const nextPage = currentPage + 1
      try {
        await dispatch(getMissions({ page: nextPage, pageSize: 3 }))
        setCurrentPage(nextPage) // Mettez à jour currentPage avec la nouvelle valeur
      } catch (error) {
        console.error('Error fetching missions:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  const isItemLoaded = (index: number) => index < missions.length

  const handleLoadMoreIfNeeded = () => {
    if (observerRef.current && hasMore && !loading) {
      const observer = new IntersectionObserver(
        (entries) => {
          const lastItemObserver = entries[entries.length - 2] // Avant-dernier élément
          if (lastItemObserver.isIntersecting) {
            loadMoreItems()
          }
        },
        { threshold: 1.0 }
      )

      // Observez tous les éléments de la liste, sauf le premier
      const items = document.querySelectorAll('.item')
      items.forEach((item, index) => {
        if (index !== 0) {
          observer.observe(item)
        }
      })
    }
  }

  const MissionRow: React.FC<{ index: number; style: React.CSSProperties }> = ({
    index,
    style,
  }) => {
    const mission = missions[index]
    return (
      <div className="item" style={style}>
        <p>{mission?.MissionName}</p>
      </div>
    )
  }

  return (
    <InfiniteLoader
      isItemLoaded={isItemLoaded}
      itemCount={missions.length + 1}
      loadMoreItems={loadMoreItems}
    >
      {({ onItemsRendered, ref }) => (
        <FixedSizeList
          height={200}
          itemCount={missions.length + 1}
          itemSize={50}
          width={300}
          onItemsRendered={onItemsRendered}
          ref={ref}
        >
          {({ index, style }) => (
            <React.Fragment key={index}>
              {index < missions.length ? (
                <MissionRow index={index} style={style} />
              ) : (
                <div ref={observerRef} style={style}>
                  {loading && <Loader />}
                </div>
              )}
            </React.Fragment>
          )}
        </FixedSizeList>
      )}
    </InfiniteLoader>
  )
}

export default Select
